import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Operation = "text" | "image" | "audio" | "transcribe";

type HistoryEntry = {
  id: string;
  operation: Operation;
  provider: string;
  model: string | null;
  status: "pending" | "completed" | "errored";
  input_payload: Record<string, unknown>;
  usage: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  ai_generation_assets?: Array<{
    id: string;
    kind: string;
    mime_type: string | null;
    content: string | null;
    metadata: Record<string, unknown> | null;
  }>;
};

type UploadedAsset = {
  path: string;
  signedUrl: string;
  mimeType: string;
  mediaType: "image" | "audio" | "video";
  size: number;
  expiresAt: string;
  name: string;
};

const OPERATION_CONFIG: Record<Operation, { label: string; description: string; functionName: string }>
  = {
    text: {
      label: "Texto",
      description: "Gera respostas textuais com base no prompt informado.",
      functionName: "ai-generate-text",
    },
    image: {
      label: "Imagem",
      description: "Cria imagens a partir de instruções textuais e referências.",
      functionName: "ai-generate-image",
    },
    audio: {
      label: "Áudio",
      description: "Gera narrações em áudio a partir de um prompt.",
      functionName: "ai-generate-audio",
    },
    transcribe: {
      label: "Transcrição",
      description: "Transcreve arquivos de áudio remotos (URL pública).",
      functionName: "ai-transcribe-audio",
    },
  };

function useAiHistory(tenantId?: string | null) {
  return useQuery({
    queryKey: ["ai-generation-history", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as HistoryEntry[];

      const { data, error } = await supabase
        .from("ai_generation_requests")
        .select("*, ai_generation_assets(*)")
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return (data ?? []) as HistoryEntry[];
    },
    enabled: !!tenantId,
  });
}

function formatDuration(createdAt?: string | null, completedAt?: string | null) {
  if (!createdAt || !completedAt) return null;
  const start = new Date(createdAt).getTime();
  const end = new Date(completedAt).getTime();
  const delta = end - start;
  if (Number.isNaN(delta) || delta < 0) return null;
  return `${(delta / 1000).toFixed(1)}s`;
}

function AssetPreview({ asset }: { asset: NonNullable<HistoryEntry["ai_generation_assets"]>[number] }) {
  if (asset.kind === "text") {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Texto gerado</Label>
        <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
          {asset.content}
        </pre>
      </div>
    );
  }

  if (asset.kind === "image" && asset.content) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Imagem gerada</Label>
        <img
          src={asset.content}
          alt="Imagem gerada por IA"
          className="rounded-md border"
        />
      </div>
    );
  }

  if (asset.kind === "audio" && asset.content) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Áudio gerado</Label>
        <audio controls src={asset.content} className="w-full" />
      </div>
    );
  }

  if (asset.kind === "transcript" && asset.content) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Transcrição</Label>
        <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
          {asset.content}
        </pre>
      </div>
    );
  }

  return null;
}

export function AITab() {
  const { tenantId } = useTenant();
  const [operation, setOperation] = useState<Operation>("text");
  const [prompt, setPrompt] = useState("");
  const [providerHint, setProviderHint] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [voice, setVoice] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [referenceUpload, setReferenceUpload] = useState<UploadedAsset | null>(null);
  const [referenceFileName, setReferenceFileName] = useState("");
  const [isUploadingReference, setIsUploadingReference] = useState(false);
  const [audioUpload, setAudioUpload] = useState<UploadedAsset | null>(null);
  const [audioFileName, setAudioFileName] = useState("");
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);

  const historyQuery = useAiHistory(tenantId);

  const uploadTempAsset = useCallback(async (file: File): Promise<UploadedAsset> => {
    const formData = new FormData();
    formData.append("file", file);

    const { data, error } = await supabase.functions.invoke("ai-upload-asset", {
      body: formData,
    });

    if (error) {
      throw error;
    }

    if (!data?.success) {
      throw new Error((data?.error as string | undefined) ?? "Falha ao enviar arquivo");
    }

    if (!data.signed_url || typeof data.signed_url !== "string") {
      throw new Error("Não foi possível gerar uma URL temporária para o arquivo");
    }

    return {
      path: String(data.path),
      signedUrl: data.signed_url,
      mimeType: String(data.mime_type ?? file.type),
      mediaType: (data.media_type ?? "image") as UploadedAsset["mediaType"],
      size: Number(data.size ?? file.size),
      expiresAt: String(data.expires_at ?? new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString()),
      name: file.name,
    };
  }, []);

  const deleteTempAsset = useCallback(async (path: string) => {
    const { error, data } = await supabase.functions.invoke("ai-upload-asset", {
      method: "DELETE",
      body: { path },
    });

    if (error) {
      throw error;
    }

    if (data?.success === false) {
      throw new Error((data?.error as string | undefined) ?? "Falha ao remover arquivo");
    }
  }, []);

  const handleReferenceFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploadingReference(true);
      try {
        if (!file.type.startsWith("image/")) {
          throw new Error("Envie um arquivo de imagem válido (PNG, JPG, WEBP, SVG)");
        }

        const uploaded = await uploadTempAsset(file);
        if (uploaded.mediaType !== "image") {
          await deleteTempAsset(uploaded.path).catch(() => undefined);
          throw new Error("Tipo de mídia não suportado para referências");
        }

        if (referenceUpload) {
          await deleteTempAsset(referenceUpload.path).catch(() => undefined);
        }

        setReferenceUpload(uploaded);
        setReferenceFileName(file.name);
        setReferenceUrl("");
        toast.success("Arquivo enviado com sucesso");
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast.error(message);
      } finally {
        setIsUploadingReference(false);
        event.target.value = "";
      }
    },
    [deleteTempAsset, referenceUpload, uploadTempAsset],
  );

  const handleRemoveReferenceUpload = useCallback(async () => {
    if (!referenceUpload) return;
    setIsUploadingReference(true);
    try {
      await deleteTempAsset(referenceUpload.path);
      setReferenceUpload(null);
      setReferenceFileName("");
      toast.success("Arquivo removido");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message);
    } finally {
      setIsUploadingReference(false);
    }
  }, [deleteTempAsset, referenceUpload]);

  const handleAudioFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploadingAudio(true);
      try {
        if (!file.type.startsWith("audio/")) {
          throw new Error("Envie um arquivo de áudio válido (MP3, WAV, OGG, WEBM)");
        }

        const uploaded = await uploadTempAsset(file);
        if (uploaded.mediaType !== "audio") {
          await deleteTempAsset(uploaded.path).catch(() => undefined);
          throw new Error("Tipo de mídia não suportado para transcrição");
        }

        if (audioUpload) {
          await deleteTempAsset(audioUpload.path).catch(() => undefined);
        }

        setAudioUpload(uploaded);
        setAudioFileName(file.name);
        setAudioUrl("");
        toast.success("Áudio enviado com sucesso");
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast.error(message);
      } finally {
        setIsUploadingAudio(false);
        event.target.value = "";
      }
    },
    [audioUpload, deleteTempAsset, uploadTempAsset],
  );

  const handleRemoveAudioUpload = useCallback(async () => {
    if (!audioUpload) return;
    setIsUploadingAudio(true);
    try {
      await deleteTempAsset(audioUpload.path);
      setAudioUpload(null);
      setAudioFileName("");
      toast.success("Áudio removido");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message);
    } finally {
      setIsUploadingAudio(false);
    }
  }, [audioUpload, deleteTempAsset]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!prompt && operation !== "transcribe") {
        throw new Error("Informe um prompt");
      }
      if (operation === "transcribe" && !audioUrl && !audioUpload) {
        throw new Error("Informe uma URL pública ou envie um arquivo de áudio");
      }

      const { functionName } = OPERATION_CONFIG[operation];

      const payload: Record<string, unknown> = {};
      if (providerHint.trim()) payload.provider_hint = providerHint.trim();

      switch (operation) {
        case "text":
          payload.prompt = prompt.trim();
          const attachments: Array<Record<string, unknown>> = [];
          if (referenceUpload) {
            attachments.push({
              type: "image",
              mimeType: referenceUpload.mimeType,
              url: referenceUpload.signedUrl,
            });
          }
          if (referenceUrl.trim()) {
            attachments.push({
              type: "image",
              mimeType: "image/png",
              url: referenceUrl.trim(),
            });
          }
          if (attachments.length > 0) {
            payload.attachments = attachments;
          }
          break;
        case "image":
          payload.prompt = prompt.trim();
          const references: Array<Record<string, unknown>> = [];
          if (referenceUpload) {
            references.push({
              type: "image",
              mimeType: referenceUpload.mimeType,
              url: referenceUpload.signedUrl,
            });
          }
          if (referenceUrl.trim()) {
            references.push({
              type: "image",
              mimeType: "image/png",
              url: referenceUrl.trim(),
            });
          }
          if (references.length > 0) {
            payload.references = references;
          }
          break;
        case "audio":
          payload.prompt = prompt.trim();
          if (voice.trim()) payload.voice = voice.trim();
          break;
        case "transcribe":
          if (audioUpload) {
            payload.audio = {
              type: "audio",
              mimeType: audioUpload.mimeType,
              url: audioUpload.signedUrl,
              metadata: {
                source: "uploaded",
                path: audioUpload.path,
              },
            };
          } else if (audioUrl.trim()) {
            payload.audio = {
              type: "audio",
              mimeType: "audio/mpeg",
              url: audioUrl.trim(),
              metadata: {
                source: "url",
              },
            };
          }
          if (prompt.trim()) payload.prompt = prompt.trim();
          break;
        default:
          break;
      }

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
      });

      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error ?? "Falha ao executar função de IA");
      }

      return data;
    },
    onSuccess: () => {
      historyQuery.refetch();
      toast.success("Solicitação enviada para a IA com sucesso");
      setPrompt("");
      setReferenceUrl("");
      setReferenceUpload(null);
      setReferenceFileName("");
      if (operation === "audio") setVoice("");
      if (operation === "transcribe") setAudioUrl("");
      setAudioUpload(null);
      setAudioFileName("");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(message);
    },
  });

  const filteredHistory = useMemo(() => {
    if (!historyQuery.data) return [] as HistoryEntry[];
    return historyQuery.data.filter((item) => item.operation === operation);
  }, [historyQuery.data, operation]);

  const loading = mutation.isPending || isUploadingReference || isUploadingAudio;

  return (
    <div className="space-y-6" data-cy="ai-tab">
      <Card>
        <CardHeader>
          <CardTitle>Laboratório de IA</CardTitle>
          <CardDescription>
            Execute chamadas síncronas para texto, imagem, áudio e transcrição usando os providers habilitados para o tenant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={operation} onValueChange={(value) => setOperation(value as Operation)}>
            <TabsList className="flex-wrap">
              {Object.entries(OPERATION_CONFIG).map(([value, { label }]) => (
                <TabsTrigger key={value} value={value} className="capitalize">
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(OPERATION_CONFIG).map(([value, config]) => (
              <TabsContent key={value} value={value} className="mt-4">
                <Alert>
                  <AlertTitle>{config.label}</AlertTitle>
                  <AlertDescription>{config.description}</AlertDescription>
                </Alert>
              </TabsContent>
            ))}
          </Tabs>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-4">
              {operation !== "transcribe" && (
                <div className="space-y-2">
                  <Label htmlFor="prompt">Prompt</Label>
                  <Textarea
                    id="prompt"
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Descreva o que você deseja gerar"
                    rows={6}
                  />
                </div>
              )}

              {operation === "transcribe" && (
                <div className="space-y-2">
                  <Label htmlFor="prompt-optional">Prompt (contexto opcional)</Label>
                  <Textarea
                    id="prompt-optional"
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Contexto para melhorar a transcrição"
                    rows={4}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="provider-hint">
                  Provider preferencial
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger className="ml-2 text-xs text-muted-foreground underline-offset-4 hover:underline">
                        opcional
                      </TooltipTrigger>
                      <TooltipContent>
                        O tenant precisa estar configurado para o provider escolhido. Se vazio, usamos a configuração padrão.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  id="provider-hint"
                  placeholder="openai, gemini ou openrouter"
                  value={providerHint}
                  onChange={(event) => setProviderHint(event.target.value)}
                />
              </div>

              {(operation === "text" || operation === "image") && (
                <div className="space-y-2">
                  <Label htmlFor="reference-url">
                    URL de referência (opcional)
                    <span className="ml-2 text-xs text-muted-foreground">
                      usamos como anexo no provider
                    </span>
                  </Label>
                  <Input
                    id="reference-url"
                    placeholder="https://exemplo.com/arquivo.png"
                    value={referenceUrl}
                    onChange={(event) => setReferenceUrl(event.target.value)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="reference-file" className="text-xs text-muted-foreground">
                      Ou envie um arquivo de imagem
                    </Label>
                    <Input
                      id="reference-file"
                      type="file"
                      accept="image/*"
                      onChange={handleReferenceFileChange}
                      disabled={isUploadingReference || loading}
                    />
                    {isUploadingReference && (
                      <p className="text-xs text-muted-foreground">Enviando arquivo...</p>
                    )}
                    {referenceUpload && (
                      <div className="space-y-2 rounded-md border p-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{referenceFileName}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveReferenceUpload}
                            disabled={isUploadingReference || loading}
                          >
                            Remover
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Expira {formatDistanceToNow(new Date(referenceUpload.expiresAt), { addSuffix: true, locale: ptBR })}
                        </p>
                        <img
                          src={referenceUpload.signedUrl}
                          alt="Pré-visualização da referência"
                          className="max-h-48 w-full rounded-md border object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {operation === "audio" && (
                <div className="space-y-2">
                  <Label htmlFor="voice">
                    Voz (opcional)
                    <span className="ml-2 text-xs text-muted-foreground">depende do provider</span>
                  </Label>
                  <Input
                    id="voice"
                    placeholder="Ex: alloy, joao-br"
                    value={voice}
                    onChange={(event) => setVoice(event.target.value)}
                  />
                </div>
              )}

              {operation === "transcribe" && (
                <div className="space-y-2">
                  <Label htmlFor="audio-url">URL do áudio para transcrição</Label>
                  <Input
                    id="audio-url"
                    placeholder="https://exemplo.com/arquivo.mp3"
                    value={audioUrl}
                    onChange={(event) => setAudioUrl(event.target.value)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="audio-file" className="text-xs text-muted-foreground">
                      Ou envie um arquivo de áudio
                    </Label>
                    <Input
                      id="audio-file"
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioFileChange}
                      disabled={isUploadingAudio || loading}
                    />
                    {isUploadingAudio && (
                      <p className="text-xs text-muted-foreground">Enviando áudio...</p>
                    )}
                    {audioUpload && (
                      <div className="space-y-2 rounded-md border p-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{audioFileName}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveAudioUpload}
                            disabled={isUploadingAudio || loading}
                          >
                            Remover
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Expira {formatDistanceToNow(new Date(audioUpload.expiresAt), { addSuffix: true, locale: ptBR })}
                        </p>
                        <audio controls src={audioUpload.signedUrl} className="w-full" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button onClick={() => mutation.mutate()} disabled={loading} className="w-full">
                {loading ? "Executando..." : "Executar"}
              </Button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Histórico recente</h3>
                <Button variant="outline" size="sm" onClick={() => historyQuery.refetch()} disabled={historyQuery.isLoading}>
                  Atualizar
                </Button>
              </div>

              {historyQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Carregando histórico...</p>
              )}

              {!historyQuery.isLoading && filteredHistory.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhum registro encontrado para esta operação. Execute uma requisição para começar a montar o histórico.
                </p>
              )}

              <div className="space-y-4">
                {filteredHistory.slice(0, 10).map((entry) => (
                  <Card key={entry.id} className="border-muted">
                    <CardHeader className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {entry.operation}
                        </Badge>
                        <Badge variant={entry.status === "completed" ? "default" : entry.status === "errored" ? "destructive" : "secondary"}>
                          {entry.status}
                        </Badge>
                        {entry.provider && (
                          <Badge variant="outline">{entry.provider}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          Criado {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: ptBR })}
                        </span>
                        {entry.completed_at && (
                          <span>• Finalizado {formatDistanceToNow(new Date(entry.completed_at), { addSuffix: true, locale: ptBR })}</span>
                        )}
                        <span>• Duração {formatDuration(entry.created_at, entry.completed_at) ?? "-"}</span>
                      </div>
                      {entry.error && (
                        <Alert variant="destructive" className="border-destructive bg-destructive/10">
                          <AlertTitle>Erro reportado</AlertTitle>
                          <AlertDescription className="text-xs whitespace-pre-wrap">
                            {entry.error}
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      {entry.input_payload?.prompt && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Prompt enviado</Label>
                          <p className="whitespace-pre-wrap">{String(entry.input_payload.prompt)}</p>
                        </div>
                      )}

                      {entry.operation === "transcribe" && entry.input_payload?.audio && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Áudio</Label>
                          <a
                            href={String((entry.input_payload.audio as Record<string, unknown>).url ?? "")}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary underline"
                          >
                            Abrir áudio
                          </a>
                        </div>
                      )}

                      {entry.ai_generation_assets?.map((asset) => (
                        <AssetPreview key={asset.id} asset={asset} />
                      ))}

                      {entry.usage && Object.keys(entry.usage).length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Uso reportado</Label>
                          <pre className="rounded-md bg-muted p-2 text-xs">
                            {JSON.stringify(entry.usage, null, 2)}
                          </pre>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <Alert>
        <AlertTitle>Atenção</AlertTitle>
        <AlertDescription>
          Arquivos enviados via upload temporário expiram em até 6 horas. Use URLs públicas ou realize novo upload caso precise reutilizar o mesmo anexo posteriormente.
        </AlertDescription>
      </Alert>
    </div>
  );
}
